import {
  BannerSharedItem,
  Block,
  BlockEditorData,
} from "@babackapp/blocks-editor/types";
import {
  EmailsThemeDto,
  UpsertEmailTemplateRequestDto,
} from "@babackapp/private-api-client/types";
import type { EmailTemplateDomain } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { EmailTemplateFormData } from "../types";

export type PrepareUpsertParams = {
  data: Omit<EmailTemplateFormData, "theme"> & {
    theme?: Required<EmailsThemeDto>;
    category?: string | null;
    domain?: EmailTemplateDomain;
  };
  emailTemplateId?: string | null;
};

export type PreparedUpsertRequest = {
  body: FormData;
  hasUploads: boolean;
};

export function prepareUpsertRequest({
  data,
  emailTemplateId,
}: PrepareUpsertParams): PreparedUpsertRequest {
  const uploadsMap = data.assets?.uploads ?? {};
  const bannerUploads: { key: string; file: File }[] = [];

  const cloneContent = cloneBlockEditorData(data.content);

  const sanitizeBlocks = (blocks: Block[]): Block[] =>
    blocks
      .filter((block) => block !== null && block !== undefined)
      .map((block) => {
        // Handle banner blocks
        if (block.type === "banner") {
          const bannerBlock = block as Block<"banner">;
          const rawProperties = (bannerBlock.properties ?? {}) as {
            layout?: "single" | "double";
            items?: BannerSharedItem[];
          };

          const sanitizedPropertyItems = (rawProperties.items ?? []).map(
            (item) => {
              const result: BannerSharedItem = {
                href: item?.href ?? "",
                imageUrl: item?.imageUrl ?? null,
                imageId: item?.imageId ?? null,
                imageUploadKey: item?.imageUploadKey ?? null,
              };

              if (result.imageUrl && result.imageUrl.startsWith("blob:")) {
                result.imageUrl = null;
              }

              if (result.imageUploadKey) {
                const upload = uploadsMap[result.imageUploadKey];

                if (upload instanceof File) {
                  bannerUploads.push({
                    key: result.imageUploadKey,
                    file: upload,
                  });
                } else {
                  result.imageUploadKey = null;
                }
              }

              return result;
            },
          );

          const sanitizedProperties = {
            ...(bannerBlock.properties ?? {}),
            layout: rawProperties.layout ?? "double",
            items: sanitizedPropertyItems,
          } as Block<"banner">["properties"];

          return {
            ...bannerBlock,
            properties: sanitizedProperties,
            content: undefined,
          };
        }

        // Handle row blocks - recursively sanitize children
        if (block.type === "row") {
          const rowBlock = block as Block<"row">;
          const content = rowBlock.content as
            | { children?: Block[] }
            | undefined;

          if (content?.children) {
            return {
              ...rowBlock,
              content: {
                ...content,
                children: sanitizeBlocks(content.children),
              },
            };
          }
        }

        // Other block types - return as is
        return block;
      });

  const sanitizedContent: BlockEditorData = {
    ...cloneContent,
    beforeBlocks: sanitizeBlocks(cloneContent.beforeBlocks ?? []),
    insideBlocks: sanitizeBlocks(cloneContent.insideBlocks ?? []),
    afterBlocks: sanitizeBlocks(cloneContent.afterBlocks ?? []),
  };

  const uniqueUploadKeys = Array.from(
    new Set(bannerUploads.map(({ key }) => key)),
  );

  const uploadsForAssets: Record<string, null> = {};
  uniqueUploadKeys.forEach((key) => {
    uploadsForAssets[key] = null;
  });

  const assetsPayload =
    uniqueUploadKeys.length > 0
      ? {
          uploads: uploadsForAssets,
        }
      : undefined;

  const templatePayload: NonNullable<
    UpsertEmailTemplateRequestDto["template"]
  > = {
    title: data?.title || "Nouveau template",
    subject: data.subject,
    preview: data.preview,
    content: sanitizedContent,
    type: data.type,
    category: data.category,
    domain: data.domain ?? "tracking",
  };

  const formData = new FormData();

  if (emailTemplateId) {
    formData.append("templateId", emailTemplateId);
  }
  formData.append("template", JSON.stringify(templatePayload));
  if (assetsPayload) {
    formData.append("assets", JSON.stringify(assetsPayload));
  }
  bannerUploads.forEach(({ key, file }) => {
    formData.append(`uploads[${key}]`, file);
  });

  return { body: formData, hasUploads: bannerUploads.length > 0 };
}

function cloneBlockEditorData(content: BlockEditorData): BlockEditorData {
  if (typeof structuredClone === "function") {
    return structuredClone(content);
  }

  return JSON.parse(JSON.stringify(content)) as BlockEditorData;
}
